<template>
  <div style="min-height: 80vh">
    <v-container v-if="entityData" class="entity-page">
      <entity-header
        :entity-data="entityData"
        :entity-type="myEntityType"
        class="mb-4 mt-2"
      >
        <template v-if="isAuthorOwner" #after-title>
          <AuthorDisplayNameEditor
            :current-display-name="entityData.display_name"
            :is-owner="isAuthorOwner"
            :pending="displayNamePending"
            @update-name="handleDisplayNameUpdate"
          />
        </template>
      </entity-header>

      <v-row v-if="myEntityType === 'works'">
        <v-col :cols="12" :md="showEntityPageStats ? 7 : 12">
          <v-card variant="outlined" rounded class="bg-white">
            <v-tabs v-model="activeTab" bg-color="transparent">
              <v-tab value="details">Details</v-tab>
              <v-tab value="locations">Locations</v-tab>
            </v-tabs>

            <v-divider />

            <v-window v-model="activeTab">
              <v-window-item value="details">
                <div class="py-6">
                  <entity-new
                    :data="entityData"
                    :type="myEntityType"
                  />
                </div>
              </v-window-item>

              <v-window-item value="locations">
                <div class="pa-6">
                  <v-alert v-if="allLocations.length === 0" type="info" variant="tonal">
                    No locations available for this work.
                  </v-alert>
                  <v-row v-else>
                    <v-col
                      v-for="(location, index) in allLocations"
                      :key="index"
                      cols="12"
                      sm="6"
                      xl="4"
                      class="d-flex"
                    >
                      <v-card variant="outlined" :elevation="0" class="d-flex flex-column" style="width: 100%;">
                        <v-card-text class="py-4">
                          <!-- Use EntityNew component to show all location fields -->
                          <entity-new
                            :data="location"
                            type="locations"
                          />
                        </v-card-text>
                        
                        <!-- Chips at bottom -->
                        <v-card-actions v-if="location.isPrimary || location.isBestOa" class="d-flex justify-end">
                          <v-chip
                            v-if="location.isPrimary"
                            size="x-small"
                            variant="outlined"
                            class="mr-1"
                          >
                            primary
                          </v-chip>
                          <v-chip
                            v-if="location.isBestOa"
                            size="x-small"
                            variant="outlined"
                          >
                            best oa
                          </v-chip>
                        </v-card-actions>
                      </v-card>
                    </v-col>
                  </v-row>
                </div>
              </v-window-item>
            </v-window>
          </v-card>
        </v-col>

        <!-- Works sidebar: just the metrics block for now (FWCI, cites, cited
             by, …). Same shape as the non-works sidebar below; group-bys are
             omitted because the obvious "works grouped by X, filtered by this
             work's id" returns exactly one row. Citing/cited group-bys can
             land here later as a follow-up. -->
        <v-col v-if="showEntityPageStats" cols="12" md="5">
          <v-card flat class="rounded-o px-2 pt-4 pb-3">
            <entity-metrics
              :data="entityData"
              :type="myEntityType"
              class="entity-metrics-block mb-3 pb-3"
            />
          </v-card>
        </v-col>
      </v-row>

      <!-- Generic entity page (non-works) -->
      <v-row v-else>
        <v-col v-if="showEntityPageStats" cols="12" md="7">
          <v-card variant="outlined" class="rounded-o py-6 bg-white">
            <entity-new
              :data="entityData"
              :type="myEntityType"
            />
          </v-card>

          <v-card variant="outlined" class="rounded-o mt-3 bg-white">
            <selection-toolbar :selectable="worksSelectable" :disable-master="isAuthorOwner && worksFilterActive">
              <template #trailing>
                <v-spacer/>
                <v-chip
                  v-if="isAuthorOwner && pendingCount"
                  :variant="worksFilterActive ? 'flat' : 'outlined'"
                  :color="worksFilterActive ? 'primary' : undefined"
                  size="small"
                  class="mr-2"
                  :append-icon="worksFilterActive ? 'mdi-close-circle' : undefined"
                  @click="worksFilterActive = !worksFilterActive"
                >
                  {{ pendingCount }} pending
                </v-chip>
                <template v-if="isAuthorOwner">
                  <v-tooltip location="bottom" text="Remove works">
                    <template v-slot:activator="{ props: rmProps }">
                      <v-btn
                        v-bind="rmProps"
                        variant="text"
                        icon
                        class="oa-cur-iconbtn"
                        :disabled="!worksCuration.canRemove.value"
                        aria-label="Remove works"
                        @click="worksCuration.removeSelected()"
                      >
                        <v-icon>mdi-trash-can-outline</v-icon>
                      </v-btn>
                    </template>
                  </v-tooltip>
                  <v-menu v-model="addMenuOpen" location="bottom end">
                    <template v-slot:activator="{ props: menuProps }">
                      <v-tooltip location="bottom" text="Add works" :disabled="addMenuOpen">
                        <template v-slot:activator="{ props: addTip }">
                          <v-btn
                            v-bind="mergeProps(menuProps, addTip)"
                            variant="text"
                            icon
                            aria-label="Add works"
                          >
                            <v-icon>mdi-plus</v-icon>
                          </v-btn>
                        </template>
                      </v-tooltip>
                    </template>
                    <v-list density="compact">
                      <v-list-item title="Add from search" @click="worksCuration.openSearch()" />
                      <v-list-item title="Add from CV" @click="worksCuration.openCv()" />
                    </v-list>
                  </v-menu>
                </template>
                <v-menu location="bottom end">
                  <template v-slot:activator="{ props: kebabProps }">
                    <v-btn
                      v-bind="kebabProps"
                      variant="plain"
                      icon
                      aria-label="More"
                    >
                      <v-icon>mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>
                  <v-list density="compact">
                    <v-list-item title="View as search" @click="viewMyWorks" />
                  </v-list>
                </v-menu>
              </template>
            </selection-toolbar>

            <div v-if="isAuthorOwner">
              <template v-if="worksFilterActive">
                <div
                  v-for="(item, idx) in worksCuration.pendingAdditions.value"
                  :key="item.work.id"
                  class="oa-cur-row"
                >
                  <div class="oa-cur-body">
                    <serp-results-list-item
                      :result="item.work"
                      pending-state="add"
                      disable-select
                      :selectable="worksSelectable"
                    />
                  </div>
                  <div class="oa-cur-badge">
                    <v-btn variant="text" size="x-small" @click="worksCuration.undoAddition(idx)">
                      Cancel
                    </v-btn>
                  </div>
                </div>
                <div
                  v-for="item in worksCuration.pendingRemovalWorks.value"
                  :key="item.work.id"
                  class="oa-cur-row"
                >
                  <div class="oa-cur-body">
                    <serp-results-list-item
                      :result="item.work"
                      pending-state="remove"
                      disable-select
                      :selectable="worksSelectable"
                    />
                  </div>
                  <div class="oa-cur-badge">
                    <v-btn variant="text" size="x-small" @click="worksCuration.undoRemoval(item.work.id)">
                      Cancel
                    </v-btn>
                  </div>
                </div>
              </template>
              <div
                v-for="result in visibleResults"
                :key="result.id"
                class="oa-cur-row"
              >
                <div class="oa-cur-body">
                  <serp-results-list-item
                    :result="result"
                    :pending-state="worksCuration.isPendingRemoval(result.id) ? 'remove' : null"
                    :disable-select="worksFilterActive"
                    :selectable="worksSelectable"
                  />
                </div>
                <div v-if="worksCuration.isPendingRemoval(result.id)" class="oa-cur-badge">
                  <v-btn variant="text" size="x-small" @click="worksCuration.undoRemoval(result.id)">
                    Cancel
                  </v-btn>
                </div>
              </div>
            </div>
            <div v-else>
              <serp-results-list-item
                  v-for="result in worksResultObject.results"
                  :key="result.id"
                  :result="result"
                  :selectable="worksSelectable"
              />
            </div>

            <div
              v-if="hasMoreWorks || worksResultObject.results?.length"
              class="pa-3 text-center"
            >
              <v-btn
                variant="text"
                rounded
                size="small"
                color="primary"
                prepend-icon="mdi-chevron-down"
                :disabled="!hasMoreWorks"
                :loading="worksLoadingMore"
                @click="showMoreWorks"
              >
                Show more works
              </v-btn>
              <v-btn
                variant="text"
                rounded
                size="small"
                color="primary"
                prepend-icon="mdi-open-in-new"
                @click="viewMyWorks"
              >
                View as search
              </v-btn>
            </div>

            <template v-if="isAuthorOwner">
              <AddWorksModal
                v-model="worksCuration.searchOpen.value"
                mode="search"
                :author-name="entityData.display_name"
                :author-id="entityData.id"
                @add-work="worksCuration.onAddWork"
              />
              <AddWorksModal
                v-model="worksCuration.cvOpen.value"
                mode="cv"
                :author-name="entityData.display_name"
                :author-id="entityData.id"
                @add-work="worksCuration.onAddWork"
              />
              <ObservedNamesEditor
                v-model="observedNamesDialogOpen"
                :name-rows="observedNamesCuration.nameRows.value"
                :loading="observedNamesCuration.loading.value"
                :submitting="observedNamesCuration.submitting.value"
                :progress="observedNamesCuration.progress.value"
                @submit="onRemoveObservedNames"
              />
            </template>
          </v-card>
        </v-col>

        <v-col v-else cols="12">
          <v-card flat class="rounded-o py-6">
            <entity-new
              :data="entityData"
              :type="myEntityType"
            />
          </v-card>
        </v-col>

        <v-col v-if="showEntityPageStats" cols="12" md="5">
          <v-card flat class="rounded-o px-2 pt-4 pb-3">
            <entity-metrics
              :data="entityData"
              :type="myEntityType"
              class="entity-metrics-block mb-3 pb-3"
            />
            <group-by
              v-for="groupByKey in groupByKeys"
              :key="groupByKey"
              :filter-key="groupByKey"
              :filter-by="[myWorksFilter]"
              entity-type="works"
              :is-entity-page="true"
              class="mb-3"
            />
          </v-card>
        </v-col>
      </v-row>

    </v-container>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, mergeProps, provide } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { useHead } from '@unhead/vue';

import { api } from '@/api';
import { url } from '@/url';
import { getEntityConfig } from '@/entityConfigs';
import { createSimpleFilter, filtersAsUrlStr } from '@/filterConfigs';

import EntityNew from '@/components/Entity/EntityNew.vue';
import EntityHeader from '@/components/Entity/EntityHeader.vue';
import EntityMetrics from '@/components/Entity/EntityMetrics.vue';
import SerpResultsListItem from '@/components/SerpResultsListItem.vue';
import SelectionToolbar from '@/components/SelectionToolbar.vue';
import GroupBy from '@/components/GroupBy/GroupBy.vue';
import { useSelectionContext } from '@/composables/useSelectionContext';

// Author curation components (feature-flagged display-name editors only)
import AuthorDisplayNameEditor from '@/components/AuthorCuration/AuthorDisplayNameEditor.vue';
import ObservedNamesEditor from '@/components/AuthorCuration/ObservedNamesEditor.vue';
import AddWorksModal from '@/components/AuthorCuration/AddWorksModal.vue';
import { useAuthorWorksCuration } from '@/composables/useAuthorWorksCuration';
import { useObservedNamesCuration } from '@/composables/useObservedNamesCuration';

defineOptions({ name: 'EntityPage' });

const store = useStore();
const route = useRoute();
const router = useRouter();

const entityData = ref(null);
const myEntityType = ref(route.params.entityType);
const worksResultObject = ref({});
const worksPage = ref(1);
const worksLoadingMore = ref(false);
const activeTab = ref(route.query.tab === 'locations' ? 'locations' : 'details');

const WORKS_PER_PAGE = 25;

const hasMoreWorks = computed(() => {
  const loaded = worksResultObject.value.results?.length || 0;
  const total = worksResultObject.value.meta?.count || 0;
  return loaded > 0 && loaded < total;
});

const myEntityConfig = computed(() => myEntityType.value ? getEntityConfig(myEntityType.value) : null);

const myWorksFilter = computed(() => {
  if (!myEntityConfig.value) return null;
  // For repository sources, use locations.source.id to show all works
  // where this repo appears in any location (not just primary)
  const isRepository = myEntityType.value === 'sources' && entityData.value?.type === 'repository';
  const filterKey = isRepository ? 'locations.source.id' : myEntityConfig.value.filterKey;
  return createSimpleFilter('works', filterKey, route.params.entityId);
});

const apiPath = computed(() => `${route.params.entityType}/${route.params.entityId}`);

const groupByKeys = computed(() => {
  if (myEntityType.value === 'awards') {
    return ['publication_year', 'primary_topic.id'];
  }
  return ['publication_year', 'primary_topic.id', 'type'];
});

const isAward = computed(() => myEntityType.value === 'awards');

const showEntityPageStats = computed(() => store.state.showEntityPageStats);

// Author curation computed properties
const isAuthor = computed(() => myEntityType.value === 'authors');
const userAuthorId = computed(() => store.getters['user/userAuthorId']);
const isAuthorOwner = computed(() => {
  if (!isAuthor.value || !userAuthorId.value || !entityData.value?.id) return false;
  const normalize = (id) => (String(id || '').match(/A\d+/i) || [''])[0].toUpperCase();
  const a = normalize(userAuthorId.value);
  return !!a && a === normalize(entityData.value.id);
});
// Selection on author works powers the owner-curation flow. oxjob #187.
// (Previously also gated by the now-decommissioned selectable_serp flag.)
const worksSelectable = computed(() => isAuthorOwner.value);

// Owner-only add/remove-works curation, layered on the existing works list
// and the shared selection store. oxjob #187.
const addMenuOpen = ref(false);
const worksCuration = useAuthorWorksCuration({
  authorId: computed(() => (isAuthorOwner.value ? entityData.value?.id || '' : '')),
  authorName: computed(() => entityData.value?.display_name || ''),
  works: computed(() => worksResultObject.value.results || []),
});

// "n pending" quasi-filter: when active the list shows ONLY works with a
// pending add/remove; when inactive those works are hidden entirely.
const worksFilterActive = ref(false);
const pendingCount = computed(() => worksCuration.pendingCount.value);
watch(pendingCount, (n) => {
  if (!n) worksFilterActive.value = false;
});
const visibleResults = computed(() => {
  const results = worksResultObject.value.results || [];
  if (!isAuthorOwner.value) return results;
  if (worksFilterActive.value) {
    return results.filter((r) => worksCuration.isPendingRemoval(r.id));
  }
  return results.filter((r) => !worksCuration.isPendingRemoval(r.id));
});

// Name-level "remove observed names" (oxjob #342). Layers a name->works
// mapping + curation-derived name visibility over the SAME per-work removal
// machinery: removals funnel through `submitAuthorCurations` and `onSubmitted`
// re-runs the per-work reconcile so the "{n} pending" chip + per-work
// strikethrough pick them up. Observed names = display_name_alternatives minus
// the primary display_name (the owner can't disown their own primary name).
const observedNames = computed(() => {
  const d = entityData.value;
  if (!d) return [];
  return (d.display_name_alternatives || []).filter(
    (n) => n && n !== d.display_name
  );
});
const observedNamesCuration = useObservedNamesCuration({
  authorId: computed(() => (isAuthorOwner.value ? entityData.value?.id || '' : '')),
  observedNames,
  onSubmitted: () => worksCuration.reconcile(),
});
const observedNamesDialogOpen = ref(false);
async function openObservedNamesDialog() {
  observedNamesDialogOpen.value = true;
  await observedNamesCuration.refreshCurations();
  if (!observedNamesCuration.mapped.value) {
    await observedNamesCuration.buildMapping();
  }
}
async function onRemoveObservedNames(names) {
  await observedNamesCuration.removeNames(names);
}
// Reload persistence (EXPLORE Q2): if the owner already has remove-curations
// in flight, build the mapping once in the background so struck/hidden name
// state is reconstructed from curations — the server doc lags removals >24h.
watch(
  () => [isAuthorOwner.value, entityData.value?.id],
  async ([owner, id]) => {
    if (!owner || !id) return;
    await observedNamesCuration.refreshCurations();
    // Only pay for the full works pagination when removals are actually in
    // flight — otherwise names render normally with no mapping cost.
    if (observedNamesCuration.hasRemovals.value) {
      await observedNamesCuration.buildMapping();
    }
  },
  { immediate: true }
);
// Expose owner-curation context to the deeply-nested observed-names row
// (EntityNew -> EntityDatumRow) without prop-drilling through the shared
// generic components. EntityDatumRow injects this for filterKey
// 'display_name_alternatives'.
provide('observedNamesOwnerCuration', {
  enabled: computed(() => isAuthorOwner.value),
  isNamePending: observedNamesCuration.isNamePending,
  isNameRemoved: observedNamesCuration.isNameRemoved,
  openDialog: openObservedNamesDialog,
});
const allLocations = computed(() => {
  if (!entityData.value || myEntityType.value !== 'works') return [];
  
  const work = entityData.value;
  
  // Helper function to create a unique key for location comparison
  const getLocationKey = (loc) => {
    if (!loc) return null;
    // Use landing_page_url as unique identifier, or fallback to stringified object
    return loc.landing_page_url || JSON.stringify(loc);
  };
  
  // Just use the locations array (which already contains primary and best OA)
  // and mark which ones are special
  const locations = [];
  const primaryKey = getLocationKey(work.primary_location);
  const bestOaKey = getLocationKey(work.best_oa_location);
  
  if (work.locations && Array.isArray(work.locations)) {
    work.locations.forEach(loc => {
      const locKey = getLocationKey(loc);
      locations.push({
        ...loc,
        isPrimary: locKey === primaryKey,
        isBestOa: locKey === bestOaKey
      });
    });
  }
  
  console.log('allLocations:', locations);
  return locations;
});

const getEntityData = async () => {
  store.state.isLoading = true;
  // Clear old data first to prevent mixing location and work data
  entityData.value = null;
  
  const data = await api.get(apiPath.value);
  
  // For locations, massage the data to create entity objects for better rendering
  if (myEntityType.value === 'locations' && data) {
    // Add the id field from the route params
    if (!data.id) {
      data.id = route.params.entityId;
    }
    
    // Transform source_name + source_id into a source object
    if (data.source_id && data.source_name) {
      data.source = {
        id: data.source_id,
        display_name: data.source_name
      };
    }
    
    // Transform work_id into a work object
    if (data.work_id) {
      const shortId = data.work_id.replace('https://openalex.org/', '');
      data.work = {
        id: data.work_id,
        display_name: shortId // Show as "works/W12345" format
      };
    }
  }
  
  entityData.value = data;
  store.state.isLoading = false;
};

const fetchWorksPage = async (page) => {
  if (myEntityType.value === 'works') return null;
  if (!myEntityConfig.value) return null;
  if (!showEntityPageStats.value) return null;

  const filterString = filtersAsUrlStr([myWorksFilter.value]);
  // For awards, sort by publication year (most recent funded works first)
  // For other entities, sort by citation count (most cited works first)
  const sortOrder = isAward.value ? 'publication_year:desc' : 'cited_by_count:desc';
  const apiUrl = api.makeUrl('works', {
    filter: filterString,
    sort: sortOrder,
    'per-page': WORKS_PER_PAGE,
    page,
  }, true);

  return api.getResultsList(apiUrl);
};

const getWorks = async () => {
  worksResultObject.value = {};
  worksPage.value = 1;
  const resp = await fetchWorksPage(1);
  if (resp) worksResultObject.value = resp;
};

const showMoreWorks = async () => {
  if (worksLoadingMore.value || !hasMoreWorks.value) return;
  worksLoadingMore.value = true;
  try {
    const nextPage = worksPage.value + 1;
    const resp = await fetchWorksPage(nextPage);
    if (resp?.results?.length) {
      worksResultObject.value = {
        ...resp,
        results: [...(worksResultObject.value.results || []), ...resp.results],
      };
      worksPage.value = nextPage;
    }
  } finally {
    worksLoadingMore.value = false;
  }
};

const viewMyWorks = () => {
  console.log(myWorksFilter.value);
  return url.pushNewFilters([myWorksFilter.value], 'works');
};

// Author curation handlers (oxjob #187, partial-success aware since #291)
const displayNamePending = ref(false);
const handleDisplayNameUpdate = async (newName) => {
  try {
    const {rows, skipped, errors} = await store.dispatch('user/submitAuthorCurations', [{
      entity: 'authors',
      entity_id: entityData.value.id,
      property: 'display_name',
      action: 'replace',
      value: newName,
    }]);
    // Single-element batch: at most one of rows/skipped/errors has an item.
    if (errors.length) {
      store.commit('snackbar', errors[0].error || 'Edit failed');
      return;
    }
    if (skipped.length) {
      // No-op (was a 409 pre-#291): the display name already equals newName.
      store.commit('snackbar', skipped[0].reason);
      return;
    }
    if (rows.length) {
      displayNamePending.value = true;
      store.commit('snackbar', 'Edit submitted');
    }
  } catch (e) {
    store.commit('snackbar', e.message);
  }
};

useHead(() => ({
  title: entityData.value?.display_name
}));

onMounted(() => {
  console.log('EntityPage mounted', route.params.entityType, route.params.entityId);
  store.state.entityType = route.params.entityType;
});

watch(apiPath, async () => {
  myEntityType.value = route.params.entityType;
  await getEntityData();
  await getWorks();
}, { immediate: true });

// Sync activeTab to URL query parameter
watch(activeTab, (newTab) => {
  if (newTab === 'locations') {
    // Add tab query parameter for locations
    router.replace({ query: { ...route.query, tab: 'locations' } });
  } else {
    // Remove tab query parameter for details (default)
    const query = { ...route.query };
    delete query.tab;
    router.replace({ query });
  }
});

// Update activeTab when URL changes
watch(() => route.query.tab, (newTab) => {
  if (newTab === 'locations') {
    activeTab.value = 'locations';
  } else {
    activeTab.value = 'details';
  }
});

useSelectionContext(() => worksResultObject.value);
</script>


<style lang="scss">
.entity-page-section-title .v-toolbar__content {
  margin-inline-start: 0px !important;
  padding: 4px 12px !important;
}
.entity-page-section-title .v-toolbar-title {
  margin-inline-start: 0px !important;
}
.entity-page  .v-list .v-list-item--active {
  color: #1976d2; // primary
}
.entity-page .text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.entity-page .entity-metrics-block {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}
.entity-page .oa-cur-row {
  display: flex;
  align-items: flex-start;
}
.entity-page .oa-cur-body {
  flex: 1 1 auto;
  min-width: 0;
}
.entity-page .oa-cur-badge {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  padding: 16px 12px 0 4px;
  white-space: nowrap;
}
.entity-page .oa-cur-iconbtn.v-btn--disabled {
  background: transparent !important;
}
.entity-page .oa-cur-iconbtn.v-btn--disabled .v-btn__overlay,
.entity-page .oa-cur-iconbtn.v-btn--disabled .v-btn__underlay {
  opacity: 0 !important;
}
</style>